import "reflect-metadata";
import { Container } from "inversify";
import TYPES from "./identifiers";

import { HttpService, IHttpService } from "../repositories/shared/httpService";
import { BaseParser, IBaseParser } from "../repositories/shared/baseParser";

import { ServiceInfoRepository } from "@/repositories/serviceInfo/serviceInfoRepository";
import { ServiceInfoParser } from "@/repositories/serviceInfo/parsers/serviceParser";
import {
  IServiceInfoParser,
  IServiceInfoRepository,
} from "@/repositories/serviceInfo/serviceInfoRepository.types";

import { BookRepository } from "../repositories/book/bookRepository";
import { BookableDatesInfoParser } from "../repositories/book/parsers/bookableDatesInfoParser";
import { BookInfoParser } from "../repositories/book/parsers/bookInfoParser";
import { BookRequestParser } from "../repositories/book/parsers/bookRequestParser";
import { KakaoPayBookParser } from "@/repositories/book/parsers/kakaoPayBookParser";
import { SeatsInfoParser } from "../repositories/book/parsers/seatsInfoParser";
import { BookResultInfoParser } from "@/repositories/book/parsers/bookResultInfoParser";
import {
  IBookableDatesInfoParser,
  IBookInfoParser,
  IBookRepository,
  IBookRequestParser,
  ISeatsInfoParser,
  IKakaoPayBookParser,
  IBookResultInfoParser,
  ITempBookResultParser,
} from "../repositories/book/bookRepository.types";

import { LineInfoRepository } from "../repositories/lineInfo/lineInfoRepository";
import {
  LineInfoParser,
  TurnParser,
} from "../repositories/lineInfo/parsers/lineInfoParser";
import { BusStopLiteParser } from "../repositories/lineInfo/parsers/busStopLiteParser";
import { TurnLiteParser } from "../repositories/lineInfo/parsers/turnLiteParser";
import { LineInfoLiteParser } from "../repositories/lineInfo/parsers/lineInfoLiteParser";
import { BusStopParser } from "../repositories/lineInfo/parsers/busStopParser";
import { FavoriteLineParser } from "../repositories/lineInfo/parsers/favoriteLineParser";
import { BusLocationParser } from "../repositories/lineInfo/parsers/busLocationParser";
import { LineTypeParser } from "@/repositories/lineInfo/parsers/lineTypeParser";
import { LineDistrictParser } from "@/repositories/lineInfo/parsers/lineDistrictParser";
import {
  IBookIntervalParser,
  IBusLocationParser,
  IBusStopLayoutInfoParser,
  IBusStopParser,
  IBusStopLiteParser,
  IContactParser,
  IFavoriteBusStopParser,
  IFavoriteLineParser,
  IFilterParser,
  IFullSeatLineParser,
  ILineDistrictParser,
  ILineFareParser,
  ILineInfoLiteParser,
  ILineInfoParser,
  ILineInfoRepository,
  ILineTurnBusStopParser,
  ILineTypeParser,
  IOnBoardLineParser,
  IRoadEventsCollectionParser,
  ITmapArrivalTimeParser,
  ITurnLiteParser,
  ITurnParser,
  ILineSearchFilterParser,
} from "../repositories/lineInfo/lineInfoRepository.types";

import { BbsRepository } from "../repositories/bbs/bbsRepository";
import { NoticeInfoParser } from "../repositories/bbs/parser/noticeInfoParser";
import {
  INoticeInfoParser,
  IBbsRepository,
  INewRouteApplicationParser,
  INewRouteApplicationDetailParser,
  IQnaInfosCollectionParser,
  IQnaInfoDetailParser,
  IVocCollectionParser,
  IVocDetailParser,
  ILostFoundsCollectionParser,
  ILostFoundDetailParser,
} from "../repositories/bbs/bbsRepository.types";

import { UserRepository } from "../repositories/user/userRepository";
import { LoginParser } from "../repositories/user/parsers/loginParser";
import { FindIdParser } from "../repositories/user/parsers/findIdParser";
import { UserInfoParser } from "../repositories/user/parsers/userInfoParser";
import { NoticeConfigParser } from "../repositories/user/parsers/noticeConfigParser";
import { CompanyParser } from "../repositories/user/parsers/companyParser";
import { CoordinateParser } from "../repositories/user/parsers/coordinateParser";
import { SocialLoginParser } from "@/repositories/user/parsers/socialLoginParser";
import { QrCodeParser } from "@/repositories/user/parsers/qrCodeParser";
import { EmailDomainInfoParser } from "@/repositories/user/parsers/emailDomainParser";
import { CertInfoParser } from "@/repositories/user/parsers/certInfoParser";
import {
  IFindIdParser,
  ISocialLoginParser,
  ILoginParser,
  INoticeConfigParser,
  IUserInfoParser,
  ICompanyParser,
  ICoordinateParser,
  IUserRepository,
  IQrCodeParser,
  IEmailDomainInfoParser,
  ICertInfoParser,
  IShouldChangePasswordInfoParser,
  IRemainBookCountParser,
  IVerifyEmployeeNumberParser,
  IReportHistoryParser,
} from "../repositories/user/userRepository.types";

import { SurveyRepository } from "../repositories/survey/surveyRepository";
import { SurveyParser } from "../repositories/survey/parsers/surveyParser";
import { SurveyTargetLineParser } from "../repositories/survey/parsers/surveyTargetLineParser";
import {
  ISurveyParser,
  ISurveyTargetLineParser,
  ISurveyRepository,
  ILineDemandSurveyListParser,
  ILineDemandSurveyAnswerParser,
} from "../repositories/survey/surveyRepository.types";

import { PaymentRepository } from "@/repositories/payment/paymentRepository";
import { PaymentInfoParser } from "@/repositories/payment/parsers/paymentInfoParser";
import { PaymentInfoDetailParser } from "@/repositories/payment/parsers/paymentDetailInfoParser";
import {
  IPaymentDetailInfoParser,
  IPaymentInfoParser,
  IPaymentMethodParser,
  IPaymentRepository,
} from "@/repositories/payment/paymentRepository.types";

import { GuideRepository } from "@/repositories/guide/guideRepository";
import { GuideInfoParser } from "@/repositories/guide/parser/guideInfoParser";
import {
  IGuideInfoParser,
  IGuideRepository,
} from "@/repositories/guide/guideRepository.types";
import {
  IBoardInfoParser,
  IBoardRepository,
} from "@/repositories/board/boardRepository.types";
import { boardInfoParser } from "@/repositories/board/parser/boardInfoParser";
import { BoardRepository } from "@/repositories/board/boardRepository";
import { LineFareParser } from "@/repositories/lineInfo/parsers/lineFareParser";
import { BookIntervalParser } from "@/repositories/lineInfo/parsers/bookIntervalParser";
import { PaymentMethodParser } from "@/repositories/payment/parsers/paymentMethodParser";
import {
  ICustomCodeCollectionParser,
  ISystemCodeParser,
  ISystemRepository,
} from "@/repositories/system/systemRepository.types";
import { SystemRepository } from "@/repositories/system/systemRepository";
import { SystemCodeParser } from "@/repositories/system/parsers/systemCodeParser";
import { OnBoardLineParser } from "@/repositories/lineInfo/parsers/onBoardLineParser";
import { FilterParser } from "@/repositories/lineInfo/parsers/filterParser";
import { NewRouteApplicationDetailParser } from "@/repositories/bbs/parser/newRouteApplicationDetailParser";
import { QnaInfosCollectionParser } from "@/repositories/bbs/parser/qnaInfosCollectionParser";
import { NewRouteApplicationParser } from "@/repositories/bbs/parser/newRouteApplicationParser";
import { QnaInfoDetailParser } from "@/repositories/bbs/parser/qnaInfoDetailParser";
import { BusStopLayoutInfoParser } from "@/repositories/lineInfo/parsers/busStopLayoutInfoParser";
import { BookTempResultParser } from "@/repositories/book/parsers/bookTempResultParser";
import {
  IAddressRepository,
  IAddressSearchInfoCollectionParser,
  ICoordinateInfoParser,
  IUtmkCoordinateInfoParser,
} from "@/repositories/address/addressRepository.types";
import { AddressRepository } from "@/repositories/address/addressRepository";
import { AddressSearchInfoCollectionParser } from "@/repositories/address/parsers/addressSearchParser";
import { UtmkCoordinateInfoParser } from "@/repositories/address/parsers/utmkCoordinateParser";
import { CoordinateInfoParser } from "@/repositories/address/parsers/coordinateInfoParser";
import {
  FileDownloadParser,
  IFileDownloadParser,
} from "@/repositories/lineInfo/parsers/fileDownloadParser";
import { GetBookableDatesFactory } from "@/domain/usecases/book/factory/GetBookableDatesFactory";
import { ILineFareUseCase } from "@/domain/usecases/line/interface/ILineFareUseCase";
import { LineFareUseCase } from "@/domain/usecases/line/LineFareUseCase";
import { GetPaymentMethodsUseCase } from "@/domain/usecases/payment/interface/GetPaymentMethodsUseCase";
import { GetPaymentMethods } from "@/domain/usecases/payment/GetPaymentMethods";
import {
  GetSeatLayoutUseCase,
  GetSeatLayout,
} from "@/domain/usecases/book/GetSeatLayout";
import { IsBookableStateUseCase } from "@/domain/usecases/payment/interface/IsBookableStateUseCase";
import { IsBookableState } from "@/domain/usecases/payment/IsBookableState";
import {
  IsDailyBookCheaper,
  IsDailyBookCheaperUseCase,
} from "@/domain/usecases/book/IsDailyBookCheaper";
import {
  HasFullyBookedDate,
  HasFullyBookedDateUseCase,
} from "@/domain/usecases/book/HasFullyBookedDate";
import { RequestFreeBookUseCase } from "@/domain/usecases/payment/interface/RequestFreeBookUseCase";
import { RequestFreeBook } from "@/domain/usecases/payment/RequestFreeBook";
import {
  RequestKakaoPay,
  RequestKakaoPayUseCase,
} from "@/domain/usecases/payment/RequestKakaoPay";
import {
  RequestFailFreeBook,
  RequestFailFreeBookUseCase,
} from "@/domain/usecases/payment/RequestFailFreeBook";
import {
  RequestCancelFreeBook,
  RequestCancelFreeBookUseCase,
} from "@/domain/usecases/payment/RequestCancelFreeBook";
import {
  GetBookIntervals,
  GetBookIntervalsUseCase,
} from "@/domain/usecases/line/GetBookIntervals";
import {
  BookableDatesInfoToDateInfos,
  BookableDatesInfoToDateInfosUseCase,
} from "@/domain/usecases/book/BookableDatesInfoToDateInfos";
import {
  DownloadFile,
  DownloadFileUseCase,
} from "@/domain/usecases/file/DownloadFile";
import {
  GetFileUrl,
  GetFileUrlUseCase,
} from "@/domain/usecases/file/GetFileUrl";
import { ContactParser } from "@/repositories/lineInfo/parsers/contactParser";
import { CustomCodeCollectionParser } from "@/repositories/system/parsers/customCodeCollectionParser";
import { ShouldChangePasswordInfoParser } from "@/repositories/user/parsers/shouldChangePasswordInfoParser";
import { TmapArrivalTimeParser } from "@/repositories/lineInfo/parsers/tmapArrivalTimeParser";
import { LineTurnBusStopParser } from "@/repositories/lineInfo/parsers/lineTurnBusStopParser";
import { RemainBookCountParser } from "@/repositories/user/parsers/remainBookCountParser";
import { VocCollectionParser } from "@/repositories/bbs/parser/vocCollectionParser";
import { VocDetailParser } from "@/repositories/bbs/parser/vocDetailParser";
import { VerifyEmployeeNumber } from "@/repositories/user/parsers/verifyEmployeeNumberParser";
import { LostFoundsCollectionParser } from "@/repositories/bbs/parser/lostFoundsCollectionParser";
import { LostFoundDetailParser } from "@/repositories/bbs/parser/lostFoundDetailParser";
import { FavoriteBusStopParser } from "@/repositories/lineInfo/parsers/favoriteBusStopParser";
import { RoadEventsCollectionParser } from "@/repositories/lineInfo/parsers/roadEventsCollectionParser";
import { FullSeatLineParser } from "@/repositories/lineInfo/parsers/fullSeatLineParser";
import { LineDemandSurveyListParser } from "@/repositories/survey/parsers/lineDemandSurveyListParser";
import { LineDemandSurveyAnswerParser } from "@/repositories/survey/parsers/lineDemandSurveyAnswerParser";
import { ReportHistoryParser } from "@/repositories/user/parsers/reportHistoryParser";
import { LineSearchFilterParser } from "@/repositories/lineInfo/parsers/lineSearchFilterParser";

const container = new Container();

// HTTP
container.bind<IHttpService>(TYPES.IHttpService).to(HttpService);

// System
container.bind<ISystemCodeParser>(TYPES.ISystemCodeParser).to(SystemCodeParser);
container
  .bind<ICustomCodeCollectionParser>(TYPES.ICustomCodeCollectionParser)
  .to(CustomCodeCollectionParser);
container.bind<ISystemRepository>(TYPES.ISystemRepository).to(SystemRepository);

// ServiceInfo
container
  .bind<IServiceInfoParser>(TYPES.IServiceInfoParser)
  .to(ServiceInfoParser);
container
  .bind<IServiceInfoRepository>(TYPES.IServiceInfoRepository)
  .to(ServiceInfoRepository);

// Book
container
  .bind<IBookableDatesInfoParser>(TYPES.IBookableDatesInfoParser)
  .to(BookableDatesInfoParser);
container.bind<ISeatsInfoParser>(TYPES.ISeatsInfoParser).to(SeatsInfoParser);
container
  .bind<IBookRequestParser>(TYPES.IBookRequestParser)
  .to(BookRequestParser);
container.bind<IBookInfoParser>(TYPES.IBookInfoParser).to(BookInfoParser);
container
  .bind<IKakaoPayBookParser>(TYPES.IKakaoPayBookParser)
  .to(KakaoPayBookParser);
container
  .bind<IBookResultInfoParser>(TYPES.IBookResultInfoParser)
  .to(BookResultInfoParser);
container
  .bind<ITempBookResultParser>(TYPES.ITempBookResultParser)
  .to(BookTempResultParser);
container.bind<IBookRepository>(TYPES.IBookRepository).to(BookRepository);
container
  .bind<GetBookableDatesFactory>(TYPES.GetBookableDatesFactory)
  .to(GetBookableDatesFactory);
container
  .bind<GetSeatLayoutUseCase>(TYPES.GetSeatLayoutUseCase)
  .to(GetSeatLayout);
container
  .bind<IsDailyBookCheaperUseCase>(TYPES.IsDailyBookCheaperUseCase)
  .to(IsDailyBookCheaper);
container
  .bind<HasFullyBookedDateUseCase>(TYPES.HasFullyBookedDateUseCase)
  .to(HasFullyBookedDate);
container
  .bind<BookableDatesInfoToDateInfosUseCase>(
    TYPES.BookableDatesInfoToDateInfosUseCase
  )
  .to(BookableDatesInfoToDateInfos);

// LineInfo
container.bind<ILineInfoParser>(TYPES.ILineInfoParser).to(LineInfoParser);
container.bind<ILineTypeParser>(TYPES.ILineTypeParser).to(LineTypeParser);
container
  .bind<ILineDistrictParser>(TYPES.ILineDistrictParser)
  .to(LineDistrictParser);
container.bind<ITurnParser>(TYPES.ITurnParser).to(TurnParser);
container.bind<IBusStopParser>(TYPES.IBusStopParser).to(BusStopParser);
container
  .bind<IFavoriteLineParser>(TYPES.IFavoriteLineParser)
  .to(FavoriteLineParser);
container.bind<IBaseParser>(TYPES.IBaseParser).to(BaseParser);
container
  .bind<IBusLocationParser>(TYPES.IBusLocationParser)
  .to(BusLocationParser);
container.bind<ILineFareParser>(TYPES.ILineFareParser).to(LineFareParser);
container
  .bind<IBookIntervalParser>(TYPES.IBookIntervalParser)
  .to(BookIntervalParser);
container
  .bind<IOnBoardLineParser>(TYPES.IOnBoardLineParser)
  .to(OnBoardLineParser);
container.bind<IFilterParser>(TYPES.IFilterParser).to(FilterParser);
container
  .bind<IBusStopLayoutInfoParser>(TYPES.IBusStopLayoutInfoParser)
  .to(BusStopLayoutInfoParser);
container
  .bind<IFileDownloadParser>(TYPES.IFileDownloadParser)
  .to(FileDownloadParser);
container
  .bind<ILineInfoRepository>(TYPES.ILineInfoRepository)
  .to(LineInfoRepository);
container.bind<ILineFareUseCase>(TYPES.ILineFareUseCase).to(LineFareUseCase);
container
  .bind<GetBookIntervalsUseCase>(TYPES.GetBookIntervalsUseCase)
  .to(GetBookIntervals);
container.bind<IContactParser>(TYPES.IContactParser).to(ContactParser);
container
  .bind<ITmapArrivalTimeParser>(TYPES.ITmapArrivalTimeParser)
  .to(TmapArrivalTimeParser);
container
  .bind<ILineTurnBusStopParser>(TYPES.ILineTurnBusStopParser)
  .to(LineTurnBusStopParser);
container
  .bind<IFavoriteBusStopParser>(TYPES.IFavoriteBusStopParser)
  .to(FavoriteBusStopParser);
container
  .bind<IRoadEventsCollectionParser>(TYPES.IRoadEventsCollectionParser)
  .to(RoadEventsCollectionParser);
container
  .bind<IFullSeatLineParser>(TYPES.IFullSeatLineParser)
  .to(FullSeatLineParser);
container
  .bind<IBusStopLiteParser>(TYPES.IBusStopLiteParser)
  .to(BusStopLiteParser);
container.bind<ITurnLiteParser>(TYPES.ITurnLiteParser).to(TurnLiteParser);
container
  .bind<ILineInfoLiteParser>(TYPES.ILineInfoLiteParser)
  .to(LineInfoLiteParser);
container
  .bind<IReportHistoryParser>(TYPES.IReportHistoryParser)
  .to(ReportHistoryParser);
container
  .bind<ILineSearchFilterParser>(TYPES.ILineSearchFilterParser)
  .to(LineSearchFilterParser);

// BBS
container.bind<INoticeInfoParser>(TYPES.INoticeInfoParser).to(NoticeInfoParser);
container
  .bind<INewRouteApplicationParser>(TYPES.INewRouteApplicationParser)
  .to(NewRouteApplicationParser);
container
  .bind<INewRouteApplicationDetailParser>(
    TYPES.INewRouteApplicationDetailParser
  )
  .to(NewRouteApplicationDetailParser);
container
  .bind<IQnaInfosCollectionParser>(TYPES.IQnaInfosCollectionParser)
  .to(QnaInfosCollectionParser);
container
  .bind<IQnaInfoDetailParser>(TYPES.IQnaInfoDetailParser)
  .to(QnaInfoDetailParser);
container
  .bind<IVocCollectionParser>(TYPES.IVocCollectionParser)
  .to(VocCollectionParser);
container.bind<IVocDetailParser>(TYPES.IVocDetailParser).to(VocDetailParser);
container
  .bind<ILostFoundsCollectionParser>(TYPES.ILostFoundsCollectionParser)
  .to(LostFoundsCollectionParser);
container
  .bind<ILostFoundDetailParser>(TYPES.ILostFoundDetailParser)
  .to(LostFoundDetailParser);
container.bind<IBbsRepository>(TYPES.IBbsRepository).to(BbsRepository);

// Survey
container.bind<ISurveyParser>(TYPES.ISurveyParser).to(SurveyParser);
container
  .bind<ISurveyTargetLineParser>(TYPES.ISurveyTargetLineParser)
  .to(SurveyTargetLineParser);
container
  .bind<ILineDemandSurveyListParser>(TYPES.ILineDemandSurveyListParser)
  .to(LineDemandSurveyListParser);
container
  .bind<ILineDemandSurveyAnswerParser>(TYPES.ILineDemandSurveyAnswerParser)
  .to(LineDemandSurveyAnswerParser);
container.bind<ISurveyRepository>(TYPES.ISurveyRepository).to(SurveyRepository);

// User
container.bind<ILoginParser>(TYPES.ILoginParser).to(LoginParser);
container
  .bind<ISocialLoginParser>(TYPES.ISocialLoginParser)
  .to(SocialLoginParser);
container
  .bind<INoticeConfigParser>(TYPES.INoticeConfigParser)
  .to(NoticeConfigParser);
container.bind<IFindIdParser>(TYPES.IFindIdParser).to(FindIdParser);
container.bind<IUserInfoParser>(TYPES.IUserInfoParser).to(UserInfoParser);
container.bind<ICompanyParser>(TYPES.ICompanyParser).to(CompanyParser);
container.bind<ICoordinateParser>(TYPES.ICoordinateParser).to(CoordinateParser);
container.bind<IQrCodeParser>(TYPES.IQrCodeParser).to(QrCodeParser);
container
  .bind<IEmailDomainInfoParser>(TYPES.IEmailDomainInfoParser)
  .to(EmailDomainInfoParser);
container.bind<ICertInfoParser>(TYPES.ICertInfoParser).to(CertInfoParser);
container.bind<IUserRepository>(TYPES.IUserRepository).to(UserRepository);
container
  .bind<IShouldChangePasswordInfoParser>(TYPES.IShouldChangePasswordInfoParser)
  .to(ShouldChangePasswordInfoParser);
container
  .bind<IRemainBookCountParser>(TYPES.IRemainBookCountParser)
  .to(RemainBookCountParser);
container
  .bind<IVerifyEmployeeNumberParser>(TYPES.IVerifyEmployeeNumberParser)
  .to(VerifyEmployeeNumber);

// Payment
container
  .bind<IPaymentInfoParser>(TYPES.IPaymentInfoParser)
  .to(PaymentInfoParser);
container
  .bind<IPaymentDetailInfoParser>(TYPES.IPaymentDetailInfoParser)
  .to(PaymentInfoDetailParser);
container
  .bind<IPaymentMethodParser>(TYPES.IPaymentMethodParser)
  .to(PaymentMethodParser);
container
  .bind<IPaymentRepository>(TYPES.IPaymentRepository)
  .to(PaymentRepository);
container
  .bind<GetPaymentMethodsUseCase>(TYPES.GetPaymentMethodsUseCase)
  .to(GetPaymentMethods);
container
  .bind<IsBookableStateUseCase>(TYPES.IsBookableStateUseCase)
  .to(IsBookableState);
container
  .bind<RequestFreeBookUseCase>(TYPES.RequestFreeBookUseCase)
  .to(RequestFreeBook);
container
  .bind<RequestFailFreeBookUseCase>(TYPES.RequestFailFreeBookUseCase)
  .to(RequestFailFreeBook);
container
  .bind<RequestCancelFreeBookUseCase>(TYPES.RequestCancelFreeBookUseCase)
  .to(RequestCancelFreeBook);
container
  .bind<RequestKakaoPayUseCase>(TYPES.RequestKakaoPayUseCase)
  .to(RequestKakaoPay);

// Guide
container.bind<IGuideInfoParser>(TYPES.IGuideInfoParser).to(GuideInfoParser);
container.bind<IGuideRepository>(TYPES.IGuideRepository).to(GuideRepository);

// Board
container.bind<IBoardInfoParser>(TYPES.IBoardInfoParser).to(boardInfoParser);
container.bind<IBoardRepository>(TYPES.IBoardRepository).to(BoardRepository);

// Address
container
  .bind<IAddressRepository>(TYPES.IAddressRepository)
  .to(AddressRepository);

container
  .bind<IAddressSearchInfoCollectionParser>(
    TYPES.IAddressSearchInfoCollectionParser
  )
  .to(AddressSearchInfoCollectionParser);

container
  .bind<IUtmkCoordinateInfoParser>(TYPES.IUtmkCoordinateInfoParser)
  .to(UtmkCoordinateInfoParser);

container
  .bind<ICoordinateInfoParser>(TYPES.ICoordinateInfoParser)
  .to(CoordinateInfoParser);

// File
container.bind<DownloadFileUseCase>(TYPES.DownloadFileUseCase).to(DownloadFile);
container.bind<GetFileUrlUseCase>(TYPES.GetFileUrlUseCase).to(GetFileUrl);

export { container };
